import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import Health from './components/Admin/Health';
import { useAuth } from './components/Auth/authHelpers';
import LoginController from './components/Auth/LoginController';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import PortalLayout from './components/PortalLayout';
import CaseReportServiceFactory from './services/CaseReportServiceFactory';
import configureAmplify from './utils/amplifyConfig';
import { AppConfig, getAppConfig } from './utils/appConfig';
import { initializeAwsTracking } from './utils/awsTracking';
import { initGA } from './utils/ga4Config';
import { logPageView } from './utils/trackingUtils';

import { ContactUs } from './pages/ContactUs';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { TermsAndConditions } from './pages/TermsAndConditions';
import { RootLayout } from './components/layout/RootLayout';
import { CaseLibrary } from './components/v1.2.0/CaseLibrary';
import { CaseReportPage } from './pages/CaseReport/CaseReportPage';
import PortalLayout120 from './components/v1.2.0/PortalLayout120';

const caseReportServiceFactory = new CaseReportServiceFactory();
const config: AppConfig = getAppConfig();
const appName = config.ENV.toUpperCase() === 'PROD' ?
  'Proprio Data Apps' :
  `Proprio Data Apps (${config.ENV})`;

// Amplify is used for the authorization flow.
configureAmplify();

initializeAwsTracking(config);

// Initialize Google Analytics to track page views and user interactions.
initGA(config.GA4_TRACKING_ID);

// Track page views using this wrapper.
const PageAnalyticsTracker = ({ children }: { children: React.JSX.Element }): React.JSX.Element => {
  const location = useLocation();

  useEffect(() => {
    logPageView(location.pathname);
  }, [location]);

  return children;
};

const caseReportService = caseReportServiceFactory.create(config.ENV);

// Create a wrapper component for the case detail route
const CaseDetailRoute = (): React.JSX.Element => {
  const { caseName } = useParams<{ caseName: string }>();
  const auth = useAuth();
  const navigate = useNavigate();

  return (
    <PortalLayout
      caseName={caseName}
      caseReportService={caseReportService}
      logout={auth?.logout}
      navigate={navigate}
    />
  );
};

// Create a wrapper component for the case detail route
const CaseDetailRoute2 = (): React.JSX.Element => {
  const { caseName } = useParams<{ caseName: string }>();
  const navigate = useNavigate();

  if (!caseName) {
    navigate('/cases/library');
    return <></>;
  }

  return (
    <CaseReportPage
      caseName={caseName}
      caseReportService={caseReportService}
      onNavigate={navigate}
    />
  );
};

function App(): React.JSX.Element {
  const navigate = useNavigate();
  const auth = useAuth();

  const handleAuthSuccess = async (redirectTo: string): Promise<void> => {
    navigate(redirectTo || '/');
  };

  return (
    <PageAnalyticsTracker>
      <Routes>
        {/* v1.0.0 routes - old navigation/layout */}
        <Route path="/" element={<Navigate to="/cases" replace />} />
        <Route
          path="/cases"
          element={
            <ProtectedRoute>
              <PortalLayout
                caseReportService={caseReportService}
                logout={auth?.logout}
                navigate={navigate}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cases/:caseName"
          element={
            <ProtectedRoute>
              <CaseDetailRoute />
            </ProtectedRoute>
          }
        />

        {/* v1.2.0+ routes - new navigation/layout (see `RootLayout`) */}
        <Route element={<RootLayout />}>
          <Route path="/login" element={
            <LoginController
              appName={appName}
              onAuthSuccess={handleAuthSuccess}
            />
          } />
          <Route path="/health" element={<Health />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route
            path="/cases-new/:caseName"
            element={
              <ProtectedRoute>
                <PortalLayout120>
                  <CaseDetailRoute2 />
                </PortalLayout120>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cases/library"
            element={
              <ProtectedRoute>
                <PortalLayout120>
                  <CaseLibrary
                    env={config.ENV || ''}
                    onNavigate={(path: string)=> {navigate(path);}}
                  />
                </PortalLayout120>
              </ProtectedRoute>
            }
          />
        </Route>
      </Routes>
    </PageAnalyticsTracker>
  );
}

export default App;
